<template>
	<div class="templateListWrapper">
		<templatesListSkeleton :items="2" :loading="loadingList" />
		<el-empty :image="require('../assets/empty.svg')" v-if="!filteredProxyList.length && !loadingList"
			description="No Templates"></el-empty>
		<div v-if="!loadingList && filteredProxyList.length" :key="searchQuery + filteredProxyList.length"
			class="listWrapper">
			<LazyList defaultLoadingColor="#210f3c" :data="filteredProxyList" :itemsPerRender="15"
				containerClasses="list">
				<template v-slot="{ item }">
					<div class="item">
						<p class="templateName">
							Published by <b>{{ item.publishedBy }}</b> {{ getPublishTime(item.updatedAt) }}
						</p>
						<h4 style="max-width: 300px; text-overflow: ellipsis; white-space: nowrap; overflow: hidden">
							{{ item.name }}
						</h4>
						<img class="templatePrevImg" :src="item.prevImg" v-if="item.prevImg" />
						<div class="template-list-preview" style="height: 160px; max-height: 160px;"
							v-if="!item.prevImg && workflowTemplateMap[item.publishedWorkflowId]">
							<templatePreview :workflowObject="workflowTemplateMap[item.publishedWorkflowId]" />
						</div>
						<p style="word-break: break-word">{{ item.description }}</p>
						<div class="footer">
							<div class="searchTags">
								<el-tag style="margin-right: 5px" v-for="(tag, i) in item.searchTags" :key="tag + i"
									type="primary" effect="plain">
									{{ tag }}
								</el-tag>
							</div>
							<el-divider class="compressedDivider"></el-divider>
							<div style="float: right; margin-right: 20px">
								<span v-if="username === item.publishedBy">
									<el-button :loading="operationInProgress && templateIdToUnublish === item.publishedWorkflowId
										" @click="unpublish(item)" type="text">
										Unpublish
									</el-button>
									<el-divider direction="vertical"></el-divider>
								</span>
								<el-button :loading="templateIdToUse === item.publishedWorkflowId" @click="use(item)"
									type="text">Use Template</el-button>
							</div>
						</div>
					</div>
				</template>
			</LazyList>
		</div>
		<regularModal :noBottomPadding="true" top="5vh" width="60%" @close="
			() => {
				showTemplateDetailModal = false;
				templateIdToUse = false;
			}
		" :visible="showTemplateDetailModal">
			<createWorkflowFromTemplateForm @operationComplete="
				() => {
					showTemplateDetailModal = false;
					templateIdToUse = false;
				}
			" :workflowTemplate="workflowTemplateToPreview" />
		</regularModal>
	</div>
</template>

<script>
import regularModal from "./regularModal.vue";
import LazyList from "lazy-load-list/vue";
import { timeDifference } from "../utils/time.js";
import _ from "lodash";
import templatesListSkeleton from "./templatesListSkeleton.vue";
import createWorkflowFromTemplateForm from "./createWorkflowFromTemplateForm.vue";
import templatePreview from "./templatePreview.vue";
export default {
	components: {
		templatePreview,
		regularModal,
		LazyList,
		templatesListSkeleton,
		createWorkflowFromTemplateForm,
	},
	props: {
		list: Array,
		searchQuery: String,
		filter: Array,
		ownershipFilter: Array,
	},
	data() {
		return {
			loadingList: true,
			loadingTimeOut: 1500,
			showTemplateDetailModal: false,
			username: this.$cookies.get("username"),
			operationInProgress: false,
			templateIdToUnublish: null,
			templateIdToUse: null,
			workflowTemplateToPreview: null,
			workflowTemplateMap: {},
			loadingPreviewMap: {}
		};
	},
	computed: {
		proxyList() {
			let clonedList = _.cloneDeep(this.list);
			this.clearLoading();
			return this.loadingList
				? []
				: [...clonedList].sort((wa, wb) => new Date(wb.updatedAt).getTime() - new Date(wa.updatedAt).getTime());
		},
		filteredProxyList() {
			let filtered = this.proxyList.filter((each) => {
				return (
					each.searchTags.some((r) => this.filter.indexOf(r) >= 0) ||
					(this.ownershipFilter.includes("My Organisation") && each.relatedToOrg) ||
					(this.ownershipFilter.includes("Publicly Available") && each.public) ||
					(this.ownershipFilter.includes("Published By Me") && each.publishedBy === this.username)
				);
			});
			return this.filter.length || this.ownershipFilter.length ? filtered : this.proxyList;
		},
	},
	watch: {
		filter: function () {
			this.loadingList = true;
		},
		filteredProxyList: {
			immediate: true,
			handler(list) {
				this.loadMissingPreviews(list);
			}
		}
	},
	methods: {
		async loadMissingPreviews(list) {
			for (const item of list) {
				if (item.prevImg) continue;

				const id = item.publishedWorkflowId;

				if (this.workflowTemplateMap[id]) continue;
				if (this.loadingPreviewMap[id]) continue;

				this.loadingPreviewMap[id] = true;

				try {
					const result = await this.httpHelper(
						"RUNTIME",
						"GET",
						"template/returnOne",
						{ publishedWorkflowId: id }
					);

					if (!result?.error) {
						this.$set(this.workflowTemplateMap, id, result.response);
					}
				} catch (e) {
					console.log("preview fetch error", e);
				}

				this.loadingPreviewMap[id] = false;
			}
		},
		async use(template) {
			this.templateIdToUse = template.publishedWorkflowId;
			const result = await this.httpHelper("RUNTIME", "GET", "template/returnOne", {
				publishedWorkflowId: template.publishedWorkflowId,
			});
			if (!result?.error) {
				this.workflowTemplateToPreview = result.response;
				this.showTemplateDetailModal = true;
			} else {
				this.templateIdToUse = null;
				this.$notifyError(this, result.error);
			}
		},
		unpublish(template) {
			this.templateIdToUnublish = template.publishedWorkflowId;
			this.operationInProgress = true;
			this.$confirm("Do you wish to unpublish this workflow. Continue?", "Warning", {
				confirmButtonText: "OK",
				cancelButtonText: "Cancel",
				type: "warning",
			})
				.then(async () => {
					const result = await this.httpHelper("RUNTIME", "DELETE", "template/unpublishOne", {
						publishedWorkflowId: template.publishedWorkflowId,
					});
					this.operationInProgress = false;
					if (!result?.error) {
						this.$emit("itemRemoved", template.publishedWorkflowId);
						this.$notify({
							title: "Success",
							message: "Workflow template unpublished",
							type: "success",
							position: "bottom-left",
						});
					} else {
						this.$notifyError(this, result.error);
					}
				})
				.catch(() => {
					this.operationInProgress = false;
				});
		},
		getPublishTime(publishedAt) {
			return timeDifference(new Date(), new Date(publishedAt));
		},
		setLoading() {
			this.loadingList = true;
		},
		clearLoading() {
			setTimeout(() => {
				this.loadingList = false;
			}, this.loadingTimeOut);
		},
	},
};
</script>

<style scoped>
.templateListWrapper {
	padding: 0px;
	padding-left: 20px;
}

.listWrapper {
	height: calc(100vh - 225px);
}

.list {
	padding-top: 24px !important;
	margin: 0;
	padding-left: 2px;
	display: flex;
	align-items: stretch;
	flex-wrap: wrap;
	align-content: flex-start;
	max-height: calc(100vh - 251px);
}

.templateListWrapper /deep/ .item {
	background-color: white;
	box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
	border-radius: 4px;
	padding: 20px;
	align-items: center;
	margin-bottom: 20px;
	height: 450px !important;
	width: 400px;
	margin-right: 20px;
	position: relative;
}

.templateListWrapper /deep/.compressedDivider {
	margin-top: 10px !important;
}

.templateListWrapper /deep/.searchTags {
	display: flex;
	flex-direction: row;
	overflow: hidden;
	white-space: nowrap;
	overflow-x: scroll;
	padding-bottom: 20px;
	padding-left: 20px;
	padding-right: 20px;
	height: 52px;
	box-sizing: border-box;
}

.templateListWrapper /deep/.templatePrevImg {
	width: 100%;
	border: 2px solid black;
	border-radius: 4px;
	box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
	background-image: url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%277%27 height=%277%27 viewBox=%270 0 1 16%27%3E%3Cpath fill=%27%237e7c83%27 fill-opacity=%271%27 d=%27M1 3h1v1H1V3zm2-2h1v1H3V1z%27%3E%3C/path%3E%3C/svg%3E");
}

.templateListWrapper /deep/.templateName {
	font-size: 10px;
	position: absolute;
	top: 5px;
	right: 15px;
	max-width: 300px;
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
}

.footer {
	position: absolute;
	bottom: 55px;
	right: 0px;
	width: 100%;
	height: 70px;
}

.footer /deep/ .el-divider--horizontal {
	margin-bottom: 10px;
}

.template-list-preview #previewCanvas {
	height: 100%;
}
</style>
